import React from 'react';
import AsyncSelect from 'react-select/async';
import { useTranslation } from "react-i18next";
import { Box, Avatar, Typography, Stack, Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

const CustomerSelect = ({ value, onChange, loadOptions, inline = false, isDisabled = false, formatOptionLabel, onEditClick }) => {
  const { t } = useTranslation();
  
  // Default option renderer with multi-line layout and avatar
  const defaultFormatOptionLabel = (option, { showEdit } = {}) => {
    if (!option.Id) return option.label || option.Name;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
        {/* Avatar/Photo */}
        <Avatar
          src={option.ProfilePicture || option.Photo}
          sx={{ 
            width: 48, 
            height: 48, 
            bgcolor: '#667eea',
            flexShrink: 0
          }}
        >
          {!option.ProfilePicture && !option.Photo && <PersonIcon />}
        </Avatar>
        
        {/* Customer Details */}
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          {/* Name and Company */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {showEdit && onEditClick && !isDisabled && (
              <Tooltip title={t('edit_customer') || 'Edit Customer'} placement="top" arrow>
                <Box
                  onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onEditClick(); }}
                  sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, cursor: 'pointer', border: '1px solid #667eea', borderRadius: '3px', p: '1px 3px', color: '#667eea' }}
                >
                  <EditIcon sx={{ fontSize: 12 }} />
                </Box>
              </Tooltip>
            )}
            <Typography variant="body2" fontWeight={600} sx={{ color: '#2c3e50' }}>
              {option.Name || option.CompanyName || 'Unknown'}
              {option.CompanyName && option.CompanyName !== option.Name && (
                <span style={{ fontWeight: 400, color: '#666', marginLeft: 4 }}>
                  ({option.CompanyName})
                </span>
              )}
            </Typography>
          </Box>
          
          {/* VAT Number & Peppol ID */}
          {(option.VATNumber || option.VAT || option.vat || option.CustomerVAT || option.CustomerPeppolId || option.peppolId) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              {(option.VATNumber || option.VAT || option.vat || option.CustomerVAT) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <AccountBalanceIcon sx={{ fontSize: 13, color: '#f57c00' }} />
                  <Typography variant="caption" sx={{ color: '#555', fontWeight: 500 }}>
                    {option.VATNumber || option.VAT || option.vat || option.CustomerVAT}
                  </Typography>
                </Box>
              )}
              {(option.VATNumber || option.VAT || option.vat || option.CustomerVAT) && (option.CustomerPeppolId || option.peppolId) && (
                <Typography variant="caption" sx={{ color: '#999', mx: 0.3 }}>•</Typography>
              )}
              {(option.CustomerPeppolId || option.peppolId) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <SendIcon sx={{ fontSize: 13, color: '#2e7d32' }} />
                  <Typography variant="caption" sx={{ color: '#555', fontWeight: 500 }}>
                    {option.CustomerPeppolId || option.peppolId}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          {/* Address */}
          {(option.Address || option.Street || option.City || option.PostCode || option.PostalCode) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <LocationOnIcon sx={{ fontSize: 13, color: '#1976d2' }} />
              <Typography variant="caption" sx={{ color: '#777' }}>
                {option.Address || [option.Street, option.Box, option.City, option.PostCode || option.PostalCode, option.Country].filter(Boolean).join(', ')}
              </Typography>
            </Box>
          )}
          
          {/* Email and Phone */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {(option.Email || option.email) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <EmailIcon sx={{ fontSize: 13, color: '#1976d2' }} />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {option.Email || option.email}
                </Typography>
              </Box>
            )}
            {(option.PhoneNumber || option.Phone || option.MobileNumber) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <PhoneIcon sx={{ fontSize: 13, color: '#2e7d32' }} />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {option.PhoneNumber || option.Phone || option.MobileNumber}
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    );
  };

  // Show full detail card for selected value; inject edit icon before the name
  const handleFormatOptionLabel = (option, meta) => {
    if (meta.context === 'value') {
      return defaultFormatOptionLabel(option, { showEdit: true });
    }
    return (formatOptionLabel || defaultFormatOptionLabel)(option, meta);
  };

  const selectElement = (
    <AsyncSelect
      cacheOptions
      loadOptions={loadOptions}
      defaultOptions
      isClearable
      isDisabled={isDisabled}
      onChange={onChange}
      value={value}
      placeholder={t("search_customer_placeholder")}
      noOptionsMessage={() => t("no_customers_found") || "No customers found"}
      loadingMessage={() => t("loading") || "Loading..."}
      formatOptionLabel={handleFormatOptionLabel}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      onBlurResetsInput={false}
      onSelectResetsInput={false}
      onCloseResetsInput={false}
      styles={{
        container: base => ({ ...base, marginBottom: inline ? 0 : 8, width: '100%' }),
        control: base => ({ ...base, minHeight: inline ? '34px' : '34px', fontSize: '0.875rem' }),
        valueContainer: base => ({ ...base, paddingTop: 0, paddingBottom: 0, paddingLeft: 8 }),
        indicatorsContainer: base => ({ ...base, height: '34px' }),
        menu: base => ({ ...base, zIndex: 9999 }),
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
          ...base,
          padding: '8px 12px',
          backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#f0f0f0' : 'white',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: state.isSelected ? '#667eea' : '#f5f5f5'
          }
        }),
        menuList: base => ({
          ...base,
          maxHeight: '400px'
        })
      }}
    />
  );
  
  if (inline) {
    return selectElement;
  }
  
  return (
    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 22, color: '#1976d2', marginBottom: 12, fontWeight: 600 }}>
        {t("customer")}
      </h2>
      {selectElement}
    </section>
  );
};

export default CustomerSelect;